/**
 * Table order service.
 * Handles all API calls for table orders with no UI dependencies.
 */

import getStoreInfo, {
  decodeTableHash,
  getTableOrder
} from "@/helpers/apiHelper";
import { loadItemsFromIndexedDB } from "@/helpers/tableOrderItemHelper";

/**
 * Fetches store information by alias
 * @param {string} storeAlias - Store alias identifier
 * @returns {Promise<Response>} Fetch response
 */
export async function fetchStoreInfo(storeAlias) {
  return getStoreInfo(storeAlias);
}

/**
 * Decodes table hash to get table information
 * @param {string} tableHash - MD5 hash of table ID
 * @returns {Promise<Object>} Table data including table_id, table_name
 */
export async function fetchTableInfo(tableHash) {
  const response = await decodeTableHash(tableHash);

  if (!response || !response.data) {
    throw new Error("Invalid response from table decode endpoint");
  }

  return response.data;
}

/**
 * Fetches existing order for a table
 * @param {number} tableId - Table ID
 * @returns {Promise<Object|null>} Order data or null if no order exists
 */
export async function fetchTableOrder(tableId) {
  try {
    const response = await getTableOrder(tableId);

    if (response.data && response.data.order) {
      return response.data.order;
    }

    return null;
  } catch (error) {
    // If 404 or no order, return null instead of throwing
    if (error.response && error.response.status === 404) {
      return null;
    }
    // Re-throw other errors
    throw error;
  }
}

/**
 * Loads cart items from IndexedDB
 * @param {Function} filterFn - Optional filter function
 * @returns {Promise<Array>} Cart items from IndexedDB
 */
export async function loadCartItemsFromStorage(filterFn = null) {
  return loadItemsFromIndexedDB(filterFn);
}
